import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingCart } from 'lucide-react';
import { CartItem } from '../../api/restaurant/ordersApi';

interface CartOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  cartEntries: number;
  cartSubtotal: number;
  addToCart: (item: any) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  onOrderClick: () => void;
  // Убираем user и onLoginClick - используем данные из основного приложения
}

const currency = (n: number): string => {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0
  }).format(n);
};

const CartOffcanvas: React.FC<CartOffcanvasProps> = ({
  isOpen,
  onClose,
  cartItems,
  cartEntries,
  cartSubtotal,
  addToCart,
  removeFromCart,
  clearCart,
  onOrderClick
}) => {

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1060,
              backdropFilter: 'blur(4px)'
            }}
          />

          {/* Cart Offcanvas Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '400px',
              maxWidth: '90vw',
              backgroundColor: 'var(--modal-bg)',
              zIndex: 1061,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e5e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ShoppingCart size={24} style={{ color: 'var(--text-primary)' }} />
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  margin: 0,
                  color: 'var(--text-primary)'
                }}>
                  Корзина
                </h2>
                {cartEntries > 0 && (
                  <span style={{
                    backgroundColor: '#ef4444',
                    color: '#fff',
                    borderRadius: '12px',
                    padding: '2px 8px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {cartEntries}
                  </span>
                )}
              </div>

              <button
                onClick={onClose}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Cart Items */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '16px'
            }}>
              {cartItems.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: 'var(--text-secondary)'
                }}>
                  <ShoppingCart size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
                    Корзина пуста
                  </h3>
                  <p>Добавьте блюда из меню, чтобы оформить заказ</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {cartItems.map((item) => {
                    const itemModifiers = item.modifiers || [];
                    const modPrice = itemModifiers.reduce((s: number, m: any) => s + (m.price || 0) * (m.count || 1), 0);
                    const itemTotal = (item.price + modPrice) * item.quantity;
                    const itemKey = item.cartKey || item.id;

                    return (
                    <div
                      key={itemKey}
                      style={{
                        display: 'flex',
                        gap: '12px',
                        padding: '16px',
                        border: '1px solid #e5e5e5',
                        borderRadius: '12px',
                        backgroundColor: 'var(--input-bg)'
                      }}
                    >
                      {/* Thumbnail */}
                      {item.imageUrls && item.imageUrls.length > 0 && (
                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          <img
                            src={item.imageUrls[0]}
                            alt={item.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                      )}

                      {/* Product Info */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          margin: 0,
                          color: 'var(--text-primary)',
                          lineHeight: '1.3'
                        }}>
                          {item.name}
                        </h4>

                        {/* Модификаторы (допы) */}
                        {itemModifiers.length > 0 && (
                          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            {itemModifiers.map((mod: any) => (
                              <div key={mod.id}>+{mod.name} {mod.count > 1 ? `×${mod.count}` : ''}</div>
                            ))}
                          </div>
                        )}

                        <div style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: 'var(--text-primary)'
                        }}>
                          {currency(itemTotal)}
                        </div>

                        {/* Quantity Controls */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginTop: 'auto'
                        }}>
                          <button
                            onClick={() => removeFromCart(itemKey)}
                            style={{
                              border: '1px solid #e5e5e5',
                              backgroundColor: 'var(--modal-bg)',
                              borderRadius: '8px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <Minus size={16} />
                          </button>

                          <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            minWidth: '30px',
                            textAlign: 'center'
                          }}>
                            {item.quantity}
                          </span>

                          <button
                            onClick={() => addToCart(item)}
                            style={{
                              border: '1px solid #e5e5e5',
                              backgroundColor: 'var(--modal-bg)',
                              borderRadius: '8px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div style={{
                padding: '20px',
                borderTop: '1px solid #e5e5e5',
                backgroundColor: 'var(--card-bg)'
              }}>
                {/* Clear Cart Button */}
                <button
                  onClick={clearCart}
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginBottom: '16px',
                    border: '1px solid #e5e5e5',
                    backgroundColor: 'var(--modal-bg)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer'
                  }}
                >
                  Очистить корзину
                </button>

                {/* Total */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                  fontSize: '18px',
                  fontWeight: '700'
                }}>
                  <span>Итого:</span>
                  <span>{currency(cartSubtotal)}</span>
                </div>

                {/* Order Button */}
                <button
                  onClick={() => {
                    onClose(); // Закрываем корзину
                    onOrderClick(); // Открываем модал заказа
                  }}
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: '#000',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Оформить заказ
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartOffcanvas;